<template>
  <div class="versions-list">
    <div v-show="versionsNotEmpty" class="header">
      <div>
        <span v-if="editMode" class="selected-amount">
          {{ checkedSet.length }}
        </span>
        <span v-if="editMode" class="selected-text">
          {{ $t('model.nlu-model.sidebar.versions.versions_service.versionsSelected') }}
        </span>
      </div>
      <div>
        <span
          v-if="showDeleteBtn"
          class="delete-btn"
          @click="$refs.deleteVersionsConfirm.open()"
        >
          {{ $t('generic.delete') }}
        </span>
        <span v-if="showSelectBtn" @click="selectBtnHandler">
          {{ selectBtnLabel }}
        </span>
      </div>
    </div>
    <div v-if="versionsNotEmpty" class="body scroll">
      <div v-for="(item, index) in versionGroups" :key="index">
        <VersionsGroup
          :index="index"
          :groups="versionGroups"
          :edit-mode="editMode"
          :checked-set="checkedSet"
          :delete-ver-proc="deleteVersionsProcessing"
        />
      </div>
    </div>
    <div v-else class="placeholder">
      <p>
        {{ $t('model.nlu-model.sidebar.versions.versions_service.noVersions') }}
      </p>
    </div>
    <or-confirm
      ref="deleteVersionsConfirm"
      :title="$t('model.nlu-model.sidebar.versions.versions_service.deleteVersionsModal.title')"
      :confirm-button-text="$t('model.nlu-model.sidebar.versions.versions_service.deleteVersionsModal.confirm')"
      :deny-button-text="$t('model.nlu-model.sidebar.versions.versions_service.deleteVersionsModal.deny')"
      @confirm="onConfirm"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions, mapState } from 'vuex'
import KnowledgeBus from '@/KnowledgeBus'
import VersionsGroup from './versions_group'

const GROUP_LENGTH = 3

export default {
  created () {
    KnowledgeBus.$on('checked::clear', this.clearCheckedSet)
    KnowledgeBus.$on('checkbox::toggle', this.checkboxToggle)
    KnowledgeBus.$on('checkbox-group::toggle', this.checkboxGroupToggle)
  },

  beforeDestroy () {
    KnowledgeBus.$off('checked::clear', this.clearCheckedSet)
    KnowledgeBus.$off('checkbox::toggle', this.checkboxToggle)
    KnowledgeBus.$off('checkbox-group::toggle', this.checkboxGroupToggle)
  },

  components: { VersionsGroup },

  computed: {
    ...mapState('knowledgebase/versions', [
      'publishVersionProcessing',
      'recoverVersionProcessing',
      'deleteVersionsProcessing'
    ]),

    ...mapGetters('knowledgebase/versions', ['modelVersions']),

    selectBtnLabel () {
      return this.editMode ? this.$t('generic.cancel') : this.$t('generic.select')
    },

    versionGroups () {
      let result = []

      _.forEach(this.modelVersions, (item, index) => {
        if (index >= _.flatten(result).length) {
          if (item.name !== 'Unnamed') {
            result.push(item)
          } else {
            const group = this.getGroup(index, this.modelVersions)
            result = (group.length < GROUP_LENGTH) ? [...result, ...group] : [...result, group]
          }
        }
      })

      return result
    },

    versionsNotEmpty () {
      return !_.isEmpty(this.versionGroups)
    },

    showDeleteBtn () {
      return !_.isEmpty(this.checkedSet)
    },

    showSelectBtn () {
      return (
        !this.publishVersionProcessing.process &&
        !this.recoverVersionProcessing.process
      )
    }
  },

  data () {
    return {
      editMode: false,
      checkedSet: [],
      showConfirmModal: false
    }
  },

  methods: {
    ...mapActions('knowledgebase/versions', ['deleteVersions']),

    onConfirm () {
      this.deleteVersions({ set: this.checkedSet })
      this.selectBtnHandler()
    },

    selectBtnHandler () {
      this.clearCheckedSet()
      this.editMode = !this.editMode
    },

    clearCheckedSet () {
      this.checkedSet = []
    },

    checkboxToggle (id) {
      this.checkedSet = _.includes(this.checkedSet, id)
        ? _.filter(this.checkedSet, n => n !== id)
        : [...this.checkedSet, id]
    },

    checkboxGroupToggle (group) {
      const idSet = _.chain(group)
        .map(item => (!item.isPublish && !item.isCurrent ? item.id : null))
        .compact()
        .value()

      if (_.every(idSet, id => _.includes(this.checkedSet, id))) {
        this.checkedSet = _.filter(
          this.checkedSet,
          item => !_.includes(idSet, item)
        )
      } else {
        _.forEach(idSet, id => {
          if (!_.includes(this.checkedSet, id)) {
            this.checkedSet.push(id)
          }
        })
      }
    },

    getGroup (start, arr) {
      let l = 0
      _.forEach(_.slice(arr, start, arr.length), item => {
        if (item.name === 'Unnamed') {
          l++

          return true
        }

        return false
      })

      return _.slice(arr, start, start + l)
    }
  }
}
</script>

<style lang="scss" scoped>
.versions-list {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;

  .header {
    display: flex;
    justify-content: space-between;
    height: 32px;
    width: 100%;
    padding: 8px 16px;

    span {
      color: $or-grey-dark;
      font-size: 12px;
      transition: all 0.2s ease;

      &.selected-amount {
        margin-right: 4px;
        font-weight: bold;
        color: $or-black-blue;
      }

      &.selected-text {
        color: $or-black-blue;
      }

      &.selected-text,
      &.selected-amount {
        &:hover {
          color: $or-black-blue;
          cursor: auto;
        }
      }

      &.delete-btn {
        color: $or-red;
        margin-right: 8px;

        &:hover {
          color: darken($or-red, 10%);
        }
      }

      &:hover {
        color: darken($or-grey-dark, 10%);
        cursor: pointer;
      }
    }
  }

  .body {
    display: flex;
    flex-direction: column;
    overflow-x: scroll;
    overflow: auto;
  }

  .placeholder {
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 100%;

    p {
      text-align: center;
      font-weight: bold;
      font-size: 16px;
      color: $or-black-blue;
      margin-bottom: 32px;

      span {
        font-size: 12px;
        font-weight: normal;
        line-height: 32px;
      }
    }
  }
}
</style>
